// (Copyright) Confluent, Inc.

import React, { Component, type ReactNode } from 'react';
import styled, { type CSSProp } from 'styled-components';

import { truncate } from '../../../utils/string-util';
import Popover, { type PopoverPlacement } from '../Popover';

// The number of milliseconds to wait before hiding the tooltip.
const TOOLTIP_LEAVE_DELAY = 500;

const ContentWrapper = styled.div<any>`
  display: ${(props: { flex: boolean; inline: boolean }) =>
    props.inline ? 'inline-block' : props.flex ? 'flex' : 'block'};
  flex: 1;
  pointer-events: all;
`;
export type LegacyTooltipProps = {
  backgroundColor?: string;
  children?: ReactNode;
  className: string;
  // style that only applies to the wrapper
  contentWrapperStyle?: React.CSSProperties;
  // css is for Popover, not wrapper
  css?: CSSProp;
  // When true, waits TOOLTIP_LEAVE_DELAY ms before closing the tooltip
  delayClose?: boolean;
  disabled: boolean;
  flex?: boolean;
  inline: boolean;
  innerTipStyle?: React.CSSProperties;
  isOpen?: boolean;
  maxTextLength?: number;
  maxTipWidth?: number;
  noPadding?: boolean;
  openOnHover?: boolean;
  // https://popper.js.org/docs/v2/constructors/#placement
  preferPlace?: null | PopoverPlacement;
  showAboveModals: boolean;
  style?: React.CSSProperties;
  tipBody?: ReactNode;
  tooltipId?: string;
  width?: number;
};

/**
 * @deprecated use the Tooltip component instead
 */
export default class LegacyTooltip extends Component<
  LegacyTooltipProps,
  {
    isOpen: boolean;
  }
> {
  state = {
    isOpen: false,
  };

  static defaultProps = {
    className: '',
    delayClose: false,
    disabled: false,
    flex: false,
    inline: false,
    showAboveModals: true,
    openOnHover: true,
  };

  hideTooltipTimeoutId: number;

  handleOpen = () => {
    clearTimeout(this.hideTooltipTimeoutId);
    this.setState({
      isOpen: true,
    });
  };

  handleClose = () => {
    const leave = () => this.setState({ isOpen: false });

    if (this.props.delayClose) {
      // @ts-expect-error - TS2322 - Type 'Timeout' is not assignable to type 'number'.
      this.hideTooltipTimeoutId = setTimeout(leave, TOOLTIP_LEAVE_DELAY);
    } else {
      leave();
    }
  };

  render() {
    const {
      backgroundColor,
      children,
      className,
      contentWrapperStyle,
      disabled,
      inline,
      showAboveModals,
      style,
      tipBody,
    } = this.props;

    const { isOpen } = this.state;
    const {
      tooltipId,
      flex,
      delayClose: _,
      maxTextLength,
      width,
      openOnHover,
      ...props
    } = this.props;
    const wrappedElement =
      typeof children === 'string' && typeof maxTextLength === 'number'
        ? truncate(children, maxTextLength, 'ltr')
        : children;
    if (tipBody == null && wrappedElement != null) {
      return wrappedElement;
    }

    return (
      <Popover
        {...props}
        backgroundColor={backgroundColor}
        body={tipBody}
        isOpen={this.props.isOpen || Boolean(!disabled && isOpen && tipBody)}
        onMouseEnterCb={() => clearTimeout(this.hideTooltipTimeoutId)}
        onMouseLeaveCb={this.handleClose}
        popoverId={tooltipId}
        showAboveModals={showAboveModals}
        width={width}
      >
        <ContentWrapper
          className={className}
          flex={flex}
          inline={inline}
          onMouseEnter={openOnHover ? this.handleOpen : null}
          onMouseLeave={openOnHover ? this.handleClose : null}
          style={contentWrapperStyle == null ? style : contentWrapperStyle}
        >
          {typeof children === 'string' && typeof maxTextLength === 'number'
            ? truncate(children, maxTextLength, 'ltr')
            : children}
        </ContentWrapper>
      </Popover>
    );
  }
}
